import React from 'react';
import Moment from 'moment';
import type { AxiosError } from 'axios';

import {
  adminPaymentComplete,
  getBalanceOnPolicy,
} from '../customers/_services/BillingService';
import {
  loadOneIncByPolicyNumber,
  waitForJQueryPlugin,
} from '../../utils/appUtils';
import { manageAPIError } from '../../utils';
import { toUniversalUtcDate } from '../../utils/date.utils';
import useNotifications from '../../_lib/notifications/useNotifications';

export type ChargeMethod = 'creditCard' | 'bankAccount';

/** The policy fields a charge reads. Binders and policies both carry them. */
export interface ChargeablePolicy {
  readonly id: string;
  readonly policyNumber: string;
  readonly accountId: string;
  readonly agencyId: string;
  readonly quoteId: string;
  readonly created: string | number;
  readonly customerEmail: string;
  readonly customerFirstName: string;
  readonly customerLastName: string;
}

export interface ChargeOptions {
  /** Called with true while the OneInc modal loads, false once it shows or fails. */
  readonly onLoading?: (loading: boolean) => void;
  /** Called when the OneInc modal closes, paid or not. */
  readonly onClosed?: () => void;
  /** Renders the due date sent to OneInc as clientReferenceData2. */
  readonly formatDueDate?: (dueDate: Moment.Moment) => string;
}

interface PaymentCompleteEvent {
  readonly acknowledge?: () => void;
  readonly transactionId?: string;
  readonly transactionDate?: string;
  readonly paymentAmount?: number;
  readonly totalPaymentAmount?: number;
  readonly bankName?: string;
  readonly cardType?: string;
  readonly transactions?: ReadonlyArray<{ readonly lastFourDigits?: string }>;
}

interface OneIncKey {
  readonly url: string;
  readonly key: string;
  readonly authCode: string;
}

const CONTAINER = '#portalOneContainer';
const PORTAL_EVENTS = [
  'portalOne.load',
  'portalOne.unload',
  'portalOne.paymentComplete',
];
const OPEN_FAILED = 'Something went wrong. Please try again later';
const PAYMENT_FAILED = 'Failed to pay, please try again later';
const BILLING_ADDRESS = '6800 Koll Center Pkwy. Suite 250, Pleasanton, CA';

export const paymentCategory = (method: ChargeMethod) =>
  method === 'creditCard' ? 'CreditCard' : 'ECheck';

export const paymentCompletePayload = (
  policy: ChargeablePolicy,
  event: PaymentCompleteEvent
) => ({
  transactionId: event.transactionId,
  transactionDate: Moment(event.transactionDate).valueOf(),
  paymentAmount: event.paymentAmount,
  totalPaymentAmount: event.totalPaymentAmount,
  policyId: policy.id,
  policyNumber: policy.policyNumber,
  lastFourDigits: event.transactions?.[0]?.lastFourDigits ?? '',
  customerEmail: policy.customerEmail,
  quoteId: policy.quoteId,
  agencyId: policy.agencyId,
  policyCreated: policy.created,
  bankName: event.bankName || '',
  cardType: event.cardType || '',
});

/**
 * Opens a PortalOne session. OneInc is a third-party host, not our API, so
 * this stays outside ServiceHandler.
 */
const createPortalOneSession = (oneInc: OneIncKey) =>
  new Promise<string>((resolve, reject) => {
    const xhttp = new XMLHttpRequest();
    xhttp.overrideMimeType('application/json');
    xhttp.onreadystatechange = () => {
      if (xhttp.readyState !== 4) {
        return;
      }
      if (xhttp.status === 200) {
        resolve(JSON.parse(xhttp.responseText).PortalOneSessionKey);
      } else {
        reject(new Error(`PortalOne session failed (${xhttp.status})`));
      }
    };
    xhttp.open(
      'GET',
      `${oneInc.url}/Api/Api/Session/Create?portalOneAuthenticationKey=${oneInc.key}`,
      true
    );
    xhttp.send();
  });

/**
 * Charges a policy's outstanding balance through OneInc's hosted PortalOne
 * modal. Needs jQuery and a `<div id="portalOneContainer" />` on the page.
 */
export const useOneIncCharge = () => {
  const notifications = useNotifications();

  return React.useCallback(
    async (
      policy: ChargeablePolicy,
      method: ChargeMethod,
      options: ChargeOptions = {}
    ) => {
      const {
        onLoading,
        onClosed,
        formatDueDate = toUniversalUtcDate,
      } = options;
      const fail = (error: unknown, fallback: string) => {
        onLoading?.(false);
        notifications.error(manageAPIError(error as AxiosError, fallback));
      };

      onLoading?.(true);

      let oneInc: OneIncKey;
      let balance: unknown;
      let sessionId: string;
      try {
        ({ data: oneInc } = await loadOneIncByPolicyNumber(
          policy.policyNumber
        ));
        ({ data: balance } = await getBalanceOnPolicy({ policyId: policy.id }));
        await waitForJQueryPlugin('portalOne');
      } catch (error) {
        fail(error, OPEN_FAILED);
        return;
      }

      const $container = window.$(CONTAINER);
      // Clear a previous charge's listeners, or one payment posts twice.
      PORTAL_EVENTS.forEach((event) => $container.off(event));
      $container.on('portalOne.load', () => onLoading?.(false));
      $container.on('portalOne.unload', () => onClosed?.());
      $container.on(
        'portalOne.paymentComplete',
        (_e: unknown, event: PaymentCompleteEvent) => {
          event.acknowledge?.();
          adminPaymentComplete(
            { accountId: policy.accountId },
            paymentCompletePayload(policy, event)
          ).catch((error: unknown) => fail(error, PAYMENT_FAILED));
        }
      );
      $container.portalOne();

      try {
        sessionId = await createPortalOneSession(oneInc);
      } catch (error) {
        fail(error, OPEN_FAILED);
        return;
      }

      $container.data('portalOne').makePayment({
        paymentCategory: paymentCategory(method),
        feeContext: 'PaymentWithFee',
        amountContext: 'AmountDue',
        minAmountDue: balance,
        accountBalance: '0',
        accountGroupCode: oneInc.authCode,
        billingZip: '',
        billingAddressStreet: BILLING_ADDRESS,
        policyHolderName: `${policy.customerFirstName} ${policy.customerLastName}`,
        referenceNumber: '',
        saveOption: 'DoNotSave',
        clientReferenceData1: policy.policyNumber,
        clientReferenceData2: formatDueDate(
          Moment(policy.created).add(7, 'days')
        ),
        clientReferenceData3: 'false',
        clientReferenceData4: policy.id,
        sessionId,
        displayMode: 'modal',
        confirmationDisplay: 'true',
      });
    },
    [notifications]
  );
};
